import React from "react";
import Joi from "joi-browser";
import { toast } from "react-toastify";
import { v4 as uuid } from "uuid";
import { Button, Form, Modal, Badge, Spinner } from "react-bootstrap";
import { addTransaction, getCustomerTransactions } from "../services/transactionService";
import FormComponent from "./common/formComponent";
import { formatNumber } from "../services/util";
import { getCurrentUser } from "../services/authService";
import FloatingAddButton from "./common/floatingAddButton";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import PageLoading from "./common/pageLoading";
import { StyledTableCell, StyledTableRow } from "../services/tableStyles";

class CustomerTransactions extends FormComponent {
  state = {
    data: {
      amount: "",
      currency: "NGN",
      method: "",
      userId: this.props.match.params.id,
    },
    errors: {},
    transactions: [],
    showAddModal: false,
    loading: true,
    processing: false,
  };

  schema = {
    amount: Joi.number().min(0).required(),
    currency: Joi.string().required(),
    method: Joi.string().required(),
    userId: Joi.string().required(),
  };

  doSubmit = async () => {
    this.setState({ processing: true });
    const { data: payload, transactions: originalTransactions } = this.state;
    payload.tx_ref = `smartciti-${uuid()}`;
    const transactions = [...originalTransactions];
    const opTransaction = { ...payload };
    opTransaction.type = "Credit";
    opTransaction.status = "Pending";
    opTransaction.initiator = `[${getCurrentUser().category}]: ${getCurrentUser().fullName}`;
    transactions.unshift(opTransaction);
    this.setState({ transactions, showAddModal: false });
    toast.success("Transaction Added - Awaiting Verification");

    try {
      await addTransaction(payload);
    } catch (ex) {
      if (ex.response && ex.response.status >= 400 && ex.response.status < 500) {
        this.setState({ transactions: originalTransactions });
        toast.error(ex.response.data);
      }
    }

    const resetData = {
      amount: "",
      currency: "NGN",
      method: "",
      userId: this.props.match.params.id,
    };

    this.setState({ processing: false, data: resetData });
  };

  async componentDidMount() {
    await this.populateTransactions();
  }

  async populateTransactions() {
    const { match } = this.props;
    const customerId = match.params.id;

    try {
      const { data: transactions } = await getCustomerTransactions(customerId);
      this.setState({ transactions });
    } catch (ex) {
      if (ex.response && ex.response.status >= 400 && ex.response.status < 500) {
        toast.error(ex.response.data);
      }
    }
    this.setState({ loading: false });
  }

  handleShowAddModal = () => {
    this.setState({ showAddModal: true });
  };

  handleClose = () => {
    this.setState({ showAddModal: false, showConfirmModal: false });
  };

  render() {
    const { transactions, errors, data, showAddModal, loading, processing } = this.state;
    return (
      <React.Fragment>
        {loading && <PageLoading loading={loading} />}
        {!loading && transactions.length === 0 && <span className="float-left mt-4">No Transactions to Display</span>}
        {!loading && <FloatingAddButton classes="float-right no-border mt-2 mb-3" onClick={this.handleShowAddModal} />}

        {!loading && transactions.length > 0 && (
          <TableContainer component={Paper}>
            <Table aria-label="customized table">
              <TableHead>
                <TableRow>
                  <StyledTableCell>Date</StyledTableCell>
                  <StyledTableCell align="right">Amount</StyledTableCell>
                  <StyledTableCell align="right">Currency</StyledTableCell>
                  <StyledTableCell align="right">Type</StyledTableCell>
                  <StyledTableCell align="right">Method</StyledTableCell>
                  <StyledTableCell align="right">Status</StyledTableCell>
                  <StyledTableCell align="right">Initiator</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions.map((transaction) => (
                  <StyledTableRow key={transaction._id}>
                    <StyledTableCell component="th" scope="row">
                      {transaction.formattedDate}
                    </StyledTableCell>
                    <StyledTableCell
                      align="right"
                      className={`${transaction.type === "Credit" ? "text-success" : "text-danger"}`}
                    >
                      {`${transaction.type === "Credit" ? "+" : "-"}`}
                      &#8358;{formatNumber(parseFloat(transaction.amount).toFixed(2))}
                    </StyledTableCell>
                    <StyledTableCell align="right">{transaction.currency}</StyledTableCell>
                    <StyledTableCell align="right">{transaction.type}</StyledTableCell>
                    <StyledTableCell align="right">{transaction.method}</StyledTableCell>
                    <StyledTableCell align="right">
                      <Badge variant={`${transaction.status === "Verified" ? "success" : "danger"}`}>
                        {transaction.status}
                      </Badge>
                    </StyledTableCell>
                    <StyledTableCell align="right">{transaction.initiator}</StyledTableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Modal show={showAddModal} size="sm" onHide={this.handleClose}>
          <Modal.Header closeButton>
            <Modal.Title>Add Transaction</Modal.Title>
          </Modal.Header>
          <Form onSubmit={this.handleSubmit}>
            <Modal.Body>
              <React.Fragment>
                <Form.Group controlId="amount">
                  <Form.Label>Amount</Form.Label>
                  <Form.Control
                    name="amount"
                    onChange={this.handleChange}
                    value={data.amount}
                    size="sm"
                    type="text"
                    autoComplete="off"
                  />
                  {errors.amount && <span className="text-danger">{errors.amount}</span>}
                </Form.Group>

                <Form.Group controlId="method">
                  <Form.Label>Payment Method</Form.Label>
                  <Form.Control as="select" name="method" value={data.method} onChange={this.handleChange}>
                    <option></option>
                    <option>Cash</option>
                    <option>POS</option>
                    <option>Transfer</option>
                  </Form.Control>
                  {errors.method && <span className="text-danger">{errors.method}</span>}
                </Form.Group>
              </React.Fragment>
            </Modal.Body>

            <Modal.Footer>
              <Button onClick={this.handleClose} variant="secondary">
                Close
              </Button>
              <Button disabled={this.validate() || processing} type="submit" variant="primary">
                {processing && (
                  <Spinner as="span" className="mr-2" animation="border" size="sm" role="status" aria-hidden="true" />
                )}
                Add
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </React.Fragment>
    );
  }
}

export default CustomerTransactions;
